import React, { useState, useEffect, useMemo } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import CustomerTable from "../components/CustomerTable";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "..";
import { Link } from "react-router-dom";
import { matchesCustomer } from "../helper/customerSearch";

const CustomerPage = () => {
  const [allRows, setAllRows] = useState([]); // every contact for this location
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(100); // rows per page
  const [searchQuery, setSearchQuery] = useState("");

  const fetchContacts = async () => {
    const token = localStorage.getItem("tokenID");
    const locationId = localStorage.getItem("locationID");
    const currentUser = JSON.parse(localStorage.getItem("user") || "null");
    const role = (currentUser?.roles?.role || "").trim().toLowerCase();
    const isAdmin = role === "admin";

    setLoading(true);
    try {
      // Pull the whole location once. Search + pagination are then done
      // client-side, so typing a phone/email finds a contact no matter which
      // "page" it would fall on, and there are no per-page network round-trips
      // (which is what was triggering the DOM "insertBefore" crash).
      const { data: res } = await axios.get(`${API_URL}/api/customer/all`, {
        params: {
          locationId,
          token,
          assignedTo: isAdmin ? undefined : currentUser?.crm_id || undefined,
        },
      });

      const contacts = res.contacts || [];
      setAllRows(
        contacts.map((c) => ({
          id: c.id,
          name:
            (c.contactName ||
              `${c.firstName || ""} ${c.lastName || ""}`).trim() || "N/A",
          phone: c.phone || "N/A",
          email: c.email || "N/A",
        }))
      );
    } catch (error) {
      console.error(
        "Error fetching contacts:",
        error?.response?.data || error.message
      );
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- client-side search + pagination -------------------------------------
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return allRows;
    return allRows.filter((row) => matchesCustomer(row, q));
  }, [allRows, searchQuery]);

  const totalCount = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const safePage = Math.min(currentPage, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * limit;
    return filteredRows.slice(start, start + limit);
  }, [filteredRows, safePage, limit]);

  useEffect(() => {
    // keep currentPage valid when the filtered set shrinks
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Customers" />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">
          {searchQuery.trim()
            ? `${totalCount} match${totalCount === 1 ? "" : "es"} of ${allRows.length}`
            : `${allRows.length} total contact${allRows.length === 1 ? "" : "s"}`}
        </span>
        <Link to={"/add-customer"} className="btn btn-info">
          + Add New Contact
        </Link>
      </div>

      {loading ? (
        <div className="text-center mt-4">
          <Spinner />
        </div>
      ) : null}

      <CustomerTable
        data={pageRows}
        loading={loading}
        currentPage={safePage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        limit={limit}
        onLimitChange={handleLimitChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
      />
    </MasterLayout>
  );
};

export default CustomerPage;
